import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const ESSENTIAL_UPSELL_SELECTOR = '[data-essential-upsell-element="container"]';
const ESSENTIAL_UPSELL_HOST_SELECTOR = '[data-cart-drawer-upsell]';
const RELOCATED_UPSELL_ATTRIBUTE = 'data-cart-drawer-upsell-relocated';
const UPSELL_ANIMATION_ATTRIBUTE = 'data-cart-drawer-upsell-animate';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {MutationObserver | null} */
  #upsellObserver = null;

  /** @type {number | null} */
  #upsellRelocationFrame = null;

  /** @type {boolean} */
  #animateUpsellOnNextRelocation = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.#startUpsellObserver();
    this.#scheduleUpsellRelocation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.#upsellObserver?.disconnect();

    if (this.#upsellRelocationFrame) {
      cancelAnimationFrame(this.#upsellRelocationFrame);
      this.#upsellRelocationFrame = null;
    }
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #handleDialogOpen = () => {
    this.#startUpsellObserver();
    this.#scheduleUpsellRelocation(true);
    this.#updateStickyState();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #startUpsellObserver() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    this.#upsellObserver?.disconnect();
    this.#upsellObserver = new MutationObserver(() => this.#scheduleUpsellRelocation());
    this.#upsellObserver.observe(dialog, { childList: true, subtree: true });
  }

  #scheduleUpsellRelocation = (animate = false) => {
    this.#animateUpsellOnNextRelocation ||= animate;

    if (this.#upsellRelocationFrame) return;

    this.#upsellRelocationFrame = requestAnimationFrame(() => {
      this.#upsellRelocationFrame = null;
      this.#relocateEssentialUpsell();
    });
  };

  #relocateEssentialUpsell() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const host = dialog.querySelector(ESSENTIAL_UPSELL_HOST_SELECTOR);
    if (!host) return;

    // Essential owns this node and can re-render it after cart updates. Move the live node instead of cloning it,
    // and anchor it in the Liquid host so Shopify's cart morph keeps the checkout CTA structure intact.
    const currentUpsell = host.querySelector(ESSENTIAL_UPSELL_SELECTOR);
    const incomingUpsell = Array.from(dialog.querySelectorAll(ESSENTIAL_UPSELL_SELECTOR)).find(
      (element) => !host.contains(element)
    );
    const upsell = incomingUpsell || currentUpsell;
    if (!upsell) return;

    const shouldAnimate = this.#animateUpsellOnNextRelocation || !!incomingUpsell || upsell.parentElement !== host;

    if (incomingUpsell && currentUpsell && incomingUpsell !== currentUpsell) {
      currentUpsell.remove();
    }

    upsell.setAttribute(RELOCATED_UPSELL_ATTRIBUTE, 'true');

    if (upsell.parentElement !== host) {
      host.appendChild(upsell);
    }

    this.#animateUpsellOnNextRelocation = false;

    if (shouldAnimate) {
      this.#animateUpsell(host);
    }

    this.#updateStickyState();
  }

  #animateUpsell(host) {
    host.removeAttribute(UPSELL_ANIMATION_ATTRIBUTE);
    // Force the next attribute write to restart the CSS animation even when the host stayed non-empty.
    void host.offsetWidth;
    host.setAttribute(UPSELL_ANIMATION_ATTRIBUTE, 'true');
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
